import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, OutlineCard, useToast } from '@morressier/alembic';
import { FormProvider, useForm } from 'react-hook-form';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import _ from 'lodash';
import MultilineText from 'src/components/MultilineText';
import { FormFieldBoolean } from '.';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { parseGuidelines } from 'src/pages/Organization/public/Journal/SubmissionUpload/StandardReportingGuidelines';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import {
  FormFieldARRIVEFiles,
  FormFieldCONSORTFiles,
  FormFieldDefinition,
  FormFieldFileUploadArray,
  FormFieldStandardReportingGuidelinesDetails
} from '@publishing-engine/domain/types/article';
import DependentCheckbox from './DependentCheckbox';
import {
  useAuthorEditSubmission,
  useEditSubmissionMetadata,
  useSubmitRevision
} from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useQueryClient } from '@tanstack/react-query';
import { StandardReportingGuidelinesRadioGroup } from 'src/pages/Organization/public/Journal/SubmissionUpload/StandardReportingGuidelinesRadioGroup';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import FormFieldTextArea from 'src/components/FormField/FormFieldTextArea';
import { IFormFieldText } from '../Text';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';

interface Props {
  isOpen: boolean;
  onSubmit: (value: boolean) => void;
  onCancel: () => void;
  formField: FormFieldBoolean;
  value: boolean;
  standardReportingGuidelinesDetails: string[] | undefined;
  CONSORTFiles: ISubmissionFile[] | undefined;
  ARRIVEFiles: ISubmissionFile[] | undefined;
  CONSORTFilesConfig: FormFieldCONSORTFiles | undefined;
  ARRIVEFilesConfig: FormFieldARRIVEFiles | undefined;
  StandardReportingGuidelinesDetails: FormFieldStandardReportingGuidelinesDetails | null;
  StandardReportingGuidelinesFileDetailsConfig:
    | FormFieldFileUploadArray<'fileSet.StandardReportingGuidelinesFileDetails'>
    | undefined;
  StandardReportingGuidelinesFileDetails: ISubmissionFile[] | undefined;
  StandardReportingGuidelinesTextConfig: IFormFieldText | undefined;
  StandardReportingGuidelinesText: string | undefined;
  submissionFields:ISubmissionFieldsResponse
}

type IStandardReportingGuidelinesFormState = {
  [key: string]: boolean | string;
};

const StandardReportingGuidelinesUpdateModal: React.FC<Props> = props => {
  const {
    isOpen,
    onSubmit,
    onCancel,
    formField,
    value,
    standardReportingGuidelinesDetails,
    CONSORTFiles,
    ARRIVEFiles,
    CONSORTFilesConfig,
    ARRIVEFilesConfig,
    StandardReportingGuidelinesDetails,
    StandardReportingGuidelinesFileDetailsConfig,
    StandardReportingGuidelinesFileDetails,
    StandardReportingGuidelinesTextConfig,
    StandardReportingGuidelinesText,
    submissionFields
  } = props;

  const schema = generateValidationSchema({
    [formField.name]: formField
  });
  const {
    submissionId,
    submissionContextId,
    organizationId,
    workflowResponse,
    fieldsResponse: { fields }
  } = useSubmissionData();
  const fieldName = formField.caseDataFields.main;
  const command = fields['StandardReportingGuidelines']?.editing.isEditable
    ? fields['StandardReportingGuidelines'].editing.via
    : undefined;
  const defaultValues = _.set(
    {} as IStandardReportingGuidelinesFormState,
    fieldName,
    value
  );

  const fieldDefs: Record<string, FormFieldDefinition> = {
    [formField.name]: formField
  };

  if (StandardReportingGuidelinesTextConfig) {
    _.set(
      defaultValues,
      StandardReportingGuidelinesTextConfig.caseDataFields.main,
      StandardReportingGuidelinesText ?? ''
    );
    fieldDefs[StandardReportingGuidelinesTextConfig.name] =
      StandardReportingGuidelinesTextConfig;
  }

  const methods = useForm({
    defaultValues: defaultValues,
    resolver: ajvResolver(schema)
  });

  const methodsCheckbox = useForm<ISubmissionFormData>({
    defaultValues: {
      preference: {
        standardReportingGuidelinesDetails: standardReportingGuidelinesDetails
      },
      fileSet: {
        CONSORTFiles: CONSORTFiles,
        ARRIVEFiles: ARRIVEFiles,
        StandardReportingGuidelinesFileDetails:
          StandardReportingGuidelinesFileDetails
      }
    }
  });
  const values = methodsCheckbox.getValues();
  const toast = useToast();
  const labels = formField.params.labels;
  const guidelines = parseGuidelines(
    StandardReportingGuidelinesDetails?.params.labels.options ?? {}
  );
  const submissionCurrentState = workflowResponse.workflowData
    .currentState as ISubmissionWFState;
  const { watch, handleSubmit, control, setValue, clearErrors, unregister } =
    methods;
  const fieldValue = watch(fieldName);

  React.useEffect(() => {
    if (fieldValue === true) {
      if (StandardReportingGuidelinesTextConfig) {
        setValue(
          'preference.standardReportingGuidelinesText' as any,
          ' ' as any,
          { shouldValidate: false }
        );
      } else {
        unregister('preference.standardReportingGuidelinesText');
      }
    }
  }, [fieldValue, StandardReportingGuidelinesTextConfig, unregister]);

  React.useEffect(() => {
    if (StandardReportingGuidelinesTextConfig) {
      clearErrors(
        StandardReportingGuidelinesTextConfig.caseDataFields.main as any
      );
    }
  }, [fieldValue, StandardReportingGuidelinesTextConfig, clearErrors]);
  const options = [
    {
      value: true,
      children: (
        <>
          <Text>{labels.options['true']?.main}</Text>
          <DependentCheckbox
            guidelines={guidelines}
            methodsCheckbox={methodsCheckbox}
            submissionId={submissionId}
            CONSORTFilesConfig={CONSORTFilesConfig}
            ARRIVEFilesConfig={ARRIVEFilesConfig}
            radioValue={fieldValue}
            StandardReportingGuidelinesFileDetailsConfig={
              StandardReportingGuidelinesFileDetailsConfig
            }
            submissionFields={submissionFields}
          />
        </>
      )
    },
    {
      value: false,
      children: (
        <>
          <Text>{labels.options['false']?.main}</Text>
          <MultilineText text={labels.options['false']?.secondary || ''} />
          {fieldValue === false && StandardReportingGuidelinesTextConfig && (
            <FormFieldTextArea
              config={StandardReportingGuidelinesTextConfig}
              placeholder="Provide any additional details"
            />
          )}
        </>
      )
    }
  ];
  const queryClient = useQueryClient();
  const SubmitRevisionMutation = useSubmitRevision({
    submissionId,
    submissionContextId,
    organizationId,
    articleWorkflowId: workflowResponse.articleWorkflow.id ?? '',
    subtype: 'fields',
    invalidateSubmissionQueries: undefined
  });

  const EditSubmissionMutation = useEditSubmissionMetadata({
    articleWorkflowId: workflowResponse.articleWorkflow.id ?? '',
    submissionContextId,
    submissionId,
    organizationId
  });

  const authorEditSubmissionMutation = useAuthorEditSubmission({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: workflowResponse.articleWorkflow.id ?? '',
    subtype: 'fields',
    invalidateSubmissionQueries: undefined
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={8}>
          <form
            onSubmit={handleSubmit(async () => {
              if (fieldValue) {
                if (
                  StandardReportingGuidelinesDetails &&
                  values.preference.standardReportingGuidelinesDetails &&
                  (values.preference.standardReportingGuidelinesDetails
                    .length ?? 0) === 0
                ) {
                  return methodsCheckbox.setError(
                    `preference.standardReportingGuidelinesDetails`,
                    {
                      type: 'manual',
                      message: 'Please select at least one checkbox.'
                    }
                  );
                }
                if (
                  StandardReportingGuidelinesDetails &&
                  values.preference.standardReportingGuidelinesDetails &&
                  values.preference.standardReportingGuidelinesDetails?.includes(
                    'CONSORT Statement'
                  ) &&
                  (values.fileSet.CONSORTFiles?.length ?? 0) === 0
                ) {
                  return methodsCheckbox.setError(`fileSet.CONSORTFiles`, {
                    type: 'manual',
                    message: 'Please upload a file to proceed further'
                  });
                }
                if (
                  StandardReportingGuidelinesDetails &&
                  values.preference.standardReportingGuidelinesDetails &&
                  values.preference.standardReportingGuidelinesDetails?.includes(
                    'ARRIVE'
                  ) &&
                  (values.fileSet.ARRIVEFiles?.length ?? 0) === 0
                ) {
                  return methodsCheckbox.setError(`fileSet.ARRIVEFiles`, {
                    type: 'manual',
                    message: 'Please upload a file to proceed further'
                  });
                }
                const payload = {
                  fileSet: {
                    CONSORTFiles:
                      methodsCheckbox.getValues('fileSet.CONSORTFiles') ?? [],
                    ARRIVEFiles:
                      methodsCheckbox.getValues('fileSet.ARRIVEFiles') ?? [],
                    StandardReportingGuidelinesFileDetails:
                      methodsCheckbox.getValues(
                        'fileSet.StandardReportingGuidelinesFileDetails'
                      ) ?? []
                  },
                  preference: {
                    standardReportingGuidelines: true,
                    standardReportingGuidelinesDetails:
                      methodsCheckbox.getValues(
                        'preference.standardReportingGuidelinesDetails'
                      ) ?? []
                  }
                };
                const response =
                  submissionCurrentState === 'unsubmitted'
                    ? await authorEditSubmissionMutation.mutateAsync(payload)
                    : command === 'EditSubmission'
                    ? await EditSubmissionMutation.mutateAsync(payload)
                    : await SubmitRevisionMutation.mutateAsync(payload);
                if (response?.isSuccess) {
                  toast({
                    variant: 'success',
                    title: `${labels.main} Updated`,
                    isClosable: true
                  });
                  queryClient.invalidateQueries([
                    'submission-fields',
                    submissionId
                  ]);
                } else {
                  toast({
                    variant: 'error',
                    title: 'Error updating field',
                    description: 'There was an error updating the field'
                  });
                }
                onCancel();
              } else {
                if (StandardReportingGuidelinesTextConfig) {
                  const textFieldValue = `${
                    methods.getValues(
                      StandardReportingGuidelinesTextConfig.caseDataFields.main
                    ) ?? ''
                  }`;
                  if (
                    StandardReportingGuidelinesTextConfig.params.validations
                      ?.required &&
                    !textFieldValue.trim()
                  ) {
                    return methods.setError(
                      StandardReportingGuidelinesTextConfig.caseDataFields
                        .main as any,
                      {
                        type: 'manual',
                        message: `Additional details are required`
                      }
                    );
                  }
                  const payload = {
                    preference: {
                      standardReportingGuidelines: false,
                      standardReportingGuidelinesText: textFieldValue
                    }
                  };
                  const response =
                    submissionCurrentState === 'unsubmitted'
                      ? await authorEditSubmissionMutation.mutateAsync(payload)
                      : command === 'EditSubmission'
                      ? await EditSubmissionMutation.mutateAsync(payload)
                      : await SubmitRevisionMutation.mutateAsync(payload);
                  if (response?.isSuccess) {
                    toast({
                      variant: 'success',
                      title: `${labels.main} Updated`,
                      isClosable: true
                    });
                    queryClient.invalidateQueries([
                      'submission-fields',
                      submissionId
                    ]);
                  } else {
                    toast({
                      variant: 'error',
                      title: 'Error updating field',
                      description: 'There was an error updating the field'
                    });
                  }
                  onCancel();
                } else {
                  onSubmit(fieldValue);
                }
              }
            })}
          >
            <OutlineCard borderless padding={8} width="100%">
              <Flex flexDirection="column" gap={4}>
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingSection">{`Update ${formField.params.labels.main}`}</Text>
                </Flex>
                <Flex flexDirection="column" gap={2}>
                  <Flex flexDirection="column" gap={6} alignItems="flex-start">
                    <Flex
                      flex={1}
                      width={'100%'}
                      gap={4}
                      flexDirection="column"
                    >
                      <StandardReportingGuidelinesRadioGroup
                        control={control}
                        name={fieldName}
                        options={options}
                        gap={2}
                      />
                    </Flex>
                  </Flex>
                  <Flex gap={2} justifyContent="flex-end">
                    <Button
                      variant="secondary"
                      onClick={onCancel}
                      borderRadius="small"
                    >
                      Exit Without Saving
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      borderRadius="small"
                    >
                      Save Changes
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            </OutlineCard>
          </form>
        </Flex>
      </FormProvider>
    </Modal>
  );
};

export default StandardReportingGuidelinesUpdateModal;
